import { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useData } from './DataProvider';

/**
 * Sparkline line chart for event density over time.
 * @param {Object} props
 * @param {number} [props.height=32] - Height in px.
 * @param {number} [props.interval=5] - Bucket interval in seconds.
 */
function Sparkline({ height = 32, interval = 10 }) {
  const { sparklineData, currentVideoTime, setSeekTo } = useData();

  // Memoize bucket calculation for performance
  const { buckets, minSec, maxCount } = useMemo(() => {
    const data = sparklineData;
    if (!data || data.length === 0) return { buckets: [], minSec: 0, maxCount: 1 };
    let minSec = Infinity, maxSec = -Infinity;
    for (let i = 0; i < data.length; ++i) {
      const s = data[i].second;
      if (typeof s === 'number') {
        if (s < minSec) minSec = s;
        if (s > maxSec) maxSec = s;
      }
    }
    if (!isFinite(minSec) || !isFinite(maxSec)) return { buckets: [], minSec: 0, maxCount: 1 };
    const bucketCount = Math.ceil((maxSec - minSec + 1) / interval);
    const buckets = Array.from({ length: bucketCount }, (_, i) => ({ start: minSec + i * interval, count: 0 }));
    for (let i = 0; i < data.length; ++i) {
      const d = data[i];
      if (typeof d.second !== 'number') continue;
      const idx = Math.floor((d.second - minSec) / interval);
      if (idx >= 0 && idx < bucketCount) {
        buckets[idx].count += d.count;
      }
    }
    let maxCount = 1;
    for (const b of buckets) {
      if (b && typeof b.count === 'number' && b.count > maxCount) maxCount = b.count;
    }
    return { buckets, minSec, maxCount };
  }, [sparklineData, interval]);

  if (!buckets.length) return null;

  // X/Y scaling
  // Add margin only for y axis, not x axis, to prevent dots from being cut off at the top/bottom
  const dotRadius = 4;
  const marginY = dotRadius + 2;
  const marginX = dotRadius + 2; // Add margin for x axis to prevent horizontal cutoff
  const w = 400; // arbitrary default for viewBox, not used for actual width
  const h = height;
  const n = buckets.length;
  const xFor = i => marginX + i * ((w - 2 * marginX) / (n - 1 || 1));
  const yFor = count => h - marginY - (count / maxCount) * (h - 2 * marginY);

  // Find which bucket currentSecond is in
  const currentBucketIdx = currentVideoTime != null ? Math.floor((currentVideoTime - minSec) / interval) : -1;

  // Build line path
  const linePath = buckets.map((b, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(b.count)}`).join(' ');

  // Format seconds as mm:ss, support negative
  function formatSec(sec) {
    const sign = sec < 0 ? '-' : '';
    const abs = Math.abs(sec);
    const m = Math.floor(abs / 60);
    const s = Math.floor(abs % 60).toString().padStart(2, '0');
    return `${sign}${m}:${s}`;
  }

  // Info for current bucket
  let currentInfo = null;
  if (currentBucketIdx >= 0 && currentBucketIdx < buckets.length) {
    const b = buckets[currentBucketIdx];
    const start = b.start;
    const end = b.start + interval - 1;
    currentInfo = `${formatSec(start)} - ${formatSec(end)} (${b.count} event${b.count === 1 ? '' : 's'})`;
  }

  return (
    <Box sx={{ width: '100%', height: 'auto', overflow: 'visible', userSelect: 'none', cursor: 'default', m: 0, p: 0, mt: 0 }}>
      <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} style={{ display: 'block', margin: 0, padding: 0 }} preserveAspectRatio="none">
        {/* Line connecting dots */}
        <path d={linePath} fill="none" stroke="#1976d2" strokeWidth={2} />
        {/* Rectangles instead of dots */}
        {buckets.map((b, i) => {
          const isCurrent = i === currentBucketIdx;
          const isActionable = b.count > 0;
          const rectWidth = 10; // width of each rectangle
          const rectHeight = (isCurrent ? (dotRadius + 1) : dotRadius) * 2; // match previous dot size
          const cx = xFor(i);
          const cy = yFor(b.count);
          return (
            <rect
              key={b.start}
              x={cx - rectWidth / 2}
              y={cy - rectHeight / 2}
              width={rectWidth}
              height={rectHeight}
              fill={isCurrent ? '#1976d2' : '#fff'}
              stroke="#1976d2"
              strokeWidth={isCurrent ? 2 : 1}
              rx={2}
              style={{ cursor: isActionable ? 'pointer' : 'default' }}
              onClick={isActionable ? () => setSeekTo(b.start) : undefined}
            />
          );
        })}
      </svg>
      {currentInfo && (
        <Box mt={0} sx={{ textAlign: 'center', fontSize: 10, color: 'text.secondary', width: '100%' }}>
          {currentInfo}
        </Box>
      )}
    </Box>
  );
}

export default Sparkline;
